import React from "react";
import { useSelector } from "react-redux";
import {
    useTheme,
    Box,
    Typography,
    IconButton,
    Tooltip,
    Link,
    Divider,
} from "@mui/material";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from "@mui/icons-material/Edit";
import WcIcon from "@mui/icons-material/Wc";
import CakeIcon from "@mui/icons-material/Cake";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import { hasPermission } from "state/checkPermission";
import { LAYOUT_HEIGHT } from 'state/constants';
import { formatTZDate } from 'state/helpers';
import FlexBetween from "components/FlexBetween";
import FlexCenter from 'components/FlexCenter';


const NonCrewMemberList = ({ rows, onEdit, onDelete }) => {
    const user = useSelector((state) => state.user);
    const theme = useTheme();

    if (!Array.isArray(rows) || rows.length === 0) {
        return (
            <FlexCenter height={LAYOUT_HEIGHT}>
                <Typography color="error">Nothing to display</Typography>
            </FlexCenter>
        );
    }

   
    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {rows.map((member, index) => {
                const birthDate = formatTZDate(member.birthday);
                const inactive = member.isActive === false;

                const canEdit = hasPermission(user?.permissions, "NonCrewMembers", "update", false);
                const canDelete = hasPermission(user?.permissions, "NonCrewMembers", "delete", false);

                const iconColor = (inactive)
                    ? theme.palette.text.disabled
                    : theme.palette.primary.dark;

                return (
                    <Box
                        key={member._id || index}
                        sx={{
                            border: `1px solid ${inactive
                                ? theme.palette.neutral.light
                                : theme.palette.neutral.deep}`,
                            borderRadius: 2,
                            p: 1.5,
                            boxShadow: 1,
                            backgroundColor: inactive
                                ? theme.palette.action.hover
                                : "background.paper",
                            color: inactive
                                ? theme.palette.text.disabled
                                : theme.palette.text.primary,
                            opacity: inactive ? 0.75 : 1,
                        }}
                    >
                        <FlexBetween>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    color: inactive
                                        ? theme.palette.text.disabled
                                        : theme.palette.text.primary,
                                }}
                            >
                                {member.staffCode} - {member.staffName}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: inactive
                                        ? theme.palette.text.disabled
                                        : theme.palette.text.secondary,
                                }}
                            >
                                {member.nationality || ""}
                            </Typography>
                        </FlexBetween>

                        <Divider sx={{ my: 0.5 }} />

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <WcIcon
                                    fontSize="small"
                                    sx={{
                                        color: inactive
                                            ? theme.palette.text.disabled
                                            : member.gender === "FM"
                                                ? theme.palette.secondary.main
                                                : theme.palette.primary.dark,
                                    }}
                                />
                                <Typography variant="body2">
                                    {member.gender || "-"}
                                </Typography>
                            </Box>

                            <Box display="flex" alignItems="center" gap={1}>
                                <CakeIcon
                                    fontSize="small"
                                    sx={{
                                        color: inactive
                                            ? theme.palette.text.disabled
                                            : "#EAB308",
                                    }}
                                />
                                <Typography variant="body2">{birthDate || "-"}</Typography>
                            </Box>

                            <Box display="flex" alignItems="center" gap={1}>
                                <MailOutlineIcon
                                    fontSize="small"
                                    sx={{
                                        color: inactive
                                            ? theme.palette.text.disabled
                                            : "#0891B2",
                                    }}
                                />
                                <Typography variant="body2">{member.email || "-"}</Typography>
                            </Box>

                            <Box display="flex" alignItems="center" gap={1}>
                                <PhoneIcon
                                    fontSize="small"
                                    sx={{
                                        color: inactive
                                            ? theme.palette.text.disabled
                                            : "#16A34A",
                                    }}
                                />
                                <Typography variant="body2">
                                    {member.contactTel ? (
                                        <Link
                                            href={`tel:${member.contactTel}`}
                                            underline="hover"
                                            sx={{
                                                color: theme.palette.text.primary,
                                                cursor: 'pointer',
                                                '&:hover': { color: theme.palette.green.deep },
                                            }}
                                        >
                                            {member.contactTel}
                                        </Link>
                                    ) : (
                                        "-"
                                    )}
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="center"
                            gap={2}
                            sx={{ mt: 1 }}
                        >
                            {canDelete && (
                                <Tooltip title="Delete">
                                    <IconButton
                                        size="small"
                                        disabled={inactive}
                                        onClick={() => {
                                            hasPermission(user?.permissions, "NonCrewMembers", "delete") 
                                            && onDelete(member);
                                        }}
                                    >
                                    <DeleteForeverIcon
                                        fontSize="small"
                                        sx={{ color: iconColor }}
                                    />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {canEdit &&  (
                                <Tooltip title="Edit">
                                    <IconButton
                                        size="small"
                                        disabled={inactive}                                
                                        onClick={() => {
                                            hasPermission(user?.permissions, "NonCrewMembers", "update") 
                                            && onEdit(member);
                                        }}
                                    >
                                    <EditIcon
                                        fontSize="small"
                                        sx={{ color: iconColor }}
                                    />
                                    </IconButton>
                                </Tooltip> 
                            )}                                                                                 
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

export default NonCrewMemberList;
